import { useRef } from "react";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { MdOutlineChat } from "react-icons/md";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zE: any;
  }
}

interface OpenChatButtonProps {
  onAfterClick?: () => void;
}

export const OpenChatButton: React.FC<OpenChatButtonProps> = ({
  onAfterClick,
}) => {
  const { t } = useModifiedTranslation();
  const { selectedContactId } = useGetContactIdData();
  const contactData = useGetContactInfo(false, selectedContactId);
  const scriptLoadingInitiated = useRef(false);

  const openChat = () => {
    if (window.zE) {
      console.log(
        "Zendesk already loaded, ensuring visibility and opening widget."
      );
      window.zE("webWidget", "show");
      window.zE("webWidget", "open");
      onAfterClick?.();
    } else if (!scriptLoadingInitiated.current) {
      console.log("Zendesk not loaded, initiating script load.");
      scriptLoadingInitiated.current = true;

      const name = contactData?.data?.name;
      const email = contactData?.data?.addresses?.[0]?.email;
      const fullLanguage = contactData?.data?.locale ?? "fi";
      const language = fullLanguage.split("-")[0];

      const script = document.createElement("script");
      script.src = process.env.REACT_APP_ZENDESK_CHAT_URL ?? "";
      script.async = true;
      script.id = "ze-snippet";

      const handleLoad = () => {
        console.log("Zendesk script loaded successfully.");
        window.zE("webWidget", "setLocale", language);
        if (name && email) {
          console.log("Prefilling Zendesk widget.", { name, email });
          window.zE("webWidget", "prefill", {
            name: { value: name, readOnly: true },
            email: { value: email, readOnly: true },
          });
        } else {
          console.warn("Zendesk: Name or email missing, cannot prefill.");
        }

        window.zE("webWidget:on", "close", () => {
          console.log("Zendesk widget closed by user, hiding launcher.");
          window.zE("webWidget", "hide");
        });

        console.log("Opening Zendesk widget for the first time.");
        window.zE("webWidget", "open");
        onAfterClick?.();
      };

      const handleError = () => {
        console.error("Failed to load Zendesk script.");
        scriptLoadingInitiated.current = false;
      };

      script.addEventListener("load", handleLoad);
      script.addEventListener("error", handleError);

      console.log("Appending Zendesk script to body.");
      document.body.appendChild(script);
    } else {
      console.warn("Zendesk script is still loading, please wait...");
    }
  };

  return (
    <div
      className="flex flex-row gap-2 items-center py-1 px-4 w-full hover:bg-gray-100 rounded-md cursor-pointer"
      onClick={openChat}
    >
      <MdOutlineChat className="w-5 h-5 text-gray-600" />
      <span className="text-sm text-gray-700">{t("userMenu.support")}</span>
    </div>
  );
};
